import { Filter } from 'components/molecules/filters/types';
import { SocketPaginationParams } from 'components/molecules/kanban-board/Kanban/type';
import { Item } from 'components/organisms/kanban-board/group-by';
import { constructNormalizedTask } from 'components/organisms/tasks/common';
import { SourceType } from 'components/organisms/tasks/fields';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { TaskField } from 'stores/fields';
import {
  ApiFilter,
  ApiGroupFilter,
  customFieldKeyToString,
} from 'utils/simple-search-filter/simple-search-filter';
import { WithNotStrategicTransformer } from './with-non-strategic-partner';
import { Task } from '.';

interface CommonParams {
  rowGroup?: Item;
  columnGroup?: Item;
  filter: GroupedFilter | Filter;
  projectId?: number;
}

interface GetTaskAcrossSwimlaneParams extends CommonParams {
  swimlaneIds: number[];
  fieldList: Record<number, TaskField>;
}

interface GetTaskInSwimLaneParam extends CommonParams {
  offsetCol: number;
  rowId: number;
  colId: number;
  fieldList: Record<number, TaskField>;
}

interface UpdateDragAndDropParams extends CommonParams {
  fromRowId: number;
  fromColId: number;
  toRowId: number;
  toColId: number;
  taskId: number;
}
interface GetTaskInSwimLaneResponse {
  rowId: number;
  rowLabel: string;
  columnData: {
    [key: string]: {
      colLabel: string;
      tasks: Task[];
      innerTaskCount: number;
    };
  };
  totalTaskCount: 3;
}

interface DragAndDropResponse {
  isDragAndDropPossible: true;
  taskDetails: Task;
  swimlaneData: GetTaskInSwimLaneResponse[];
}

interface RawFetchLayoutForTaskIdResponse {
  swimlaneData: {
    rowId: number;
    rowLabel: string;
    columnData: {
      [key: string]: {
        colLabel: string;
      };
    };
  }[];
  taskDetails: Task;
}

const getFilterString = (filter: GroupedFilter | Filter) => {
  if ('groupCriteria' in filter) {
    return ApiGroupFilter.toQueryStringFormatForGroups(filter.groupCriteria);
  }

  return ApiFilter.toQueryStringFormat(filter.criteria);
};

const itemToString = (field?: Item) => {
  if (!field) {
    return '';
  }

  return field.isUserField
    ? customFieldKeyToString({
        fieldId: field.fieldId,
        sourceType: SourceType.Task,
      })
    : field.value;
};

const swimlaneTransformer = (
  res: FetchSwimlanesResponse,
  fieldList: Record<number, TaskField>,
): FetchSwimlanesResponse => {
  return res.map((r) => {
    return {
      columnData: Object.keys(r.columnData).reduce((acc, key) => {
        acc[key] = {
          ...r.columnData[key],
          tasks: Object.values(
            constructNormalizedTask({
              taskList: r.columnData[key].tasks,
              fieldList,
              isLoading: false,
            }).byId,
          ).map((task) => task[1]),
          // task: r.columnData[key].tasks,
        };

        return acc;
      }, {}),
      rowId: r.rowId,
      rowLabel: r.rowLabel,
      totalTaskCount: r.totalTaskCount,
    };
  });
};

const columnTransformer = (
  res: GetTaskInSwimLaneResponse,
  fieldList: Record<number, TaskField>,
): GetTaskInSwimLaneResponse => {
  return {
    ...res,
    columnData: Object.keys(res.columnData).reduce((acc, key) => {
      acc[key] = {
        ...res.columnData[key],
        tasks: Object.values(
          constructNormalizedTask({
            taskList: res.columnData[key].tasks,
            fieldList,
            isLoading: false,
          }).byId,
        ).map((task) => task[1]),
        // task: res.columnData[key].tasks,
      };

      return acc;
    }, {}),
  };
};

interface FetchSwimlane {
  rowId: number;
  rowLabel: string;
  columnData: {
    [key: string]: {
      colLabel: string;
      tasks: Task[];
      innerTaskCount: number;
    };
  };
  totalTaskCount: number;
}
type FetchSwimlanesResponse = FetchSwimlane[];
type FetchSwimlanesResponseWithMeta = {
  kanbanResponseModelList: FetchSwimlanesResponse;
  columnSortOrder?: number[];
};

const sortLayout = (layout: FetchSwimlanesResponse) => {
  return layout.sort((a, b) => {
    return b.totalTaskCount - a.totalTaskCount;
  });
};

class KanbanHttpClient extends WithNotStrategicTransformer {
  public async getLayout({
    rowGroup,
    columnGroup,
    filter,
    projectId,
  }: CommonParams): Promise<FetchSwimlanesResponseWithMeta> {
    if (!columnGroup) {
      return Promise.reject();
    }
    const baseApi = projectId
      ? `/tasks/projects/${projectId}/kanbanLayout`
      : 'tasks/getlightkanbanCount';

    const res = await this.get<FetchSwimlanesResponseWithMeta>(
      `${baseApi}?${getFilterString(filter)}&match=${filter.type}`,
      {
        params: {
          rowGroup: itemToString(rowGroup),
          columnGroup: itemToString(columnGroup),
        },
      },
    );

    return {
      kanbanResponseModelList: sortLayout(res.kanbanResponseModelList),
      columnSortOrder: res.columnSortOrder,
    };
  }

  public async getTaskAcrossSwimlane({
    swimlaneIds,
    rowGroup,
    columnGroup,
    fieldList,
    filter,
    projectId,
  }: GetTaskAcrossSwimlaneParams): Promise<FetchSwimlanesResponse> {
    if (!columnGroup) {
      return Promise.reject();
    }
    const baseApi = projectId
      ? `tasks/projects/${projectId}/kanbanV1ForProjectBoard`
      : 'tasks/getlightkanbanV1';

    const result = await this.get<{
      kanbanResponseModelList: FetchSwimlanesResponse;
    }>(`${baseApi}?${getFilterString(filter)}&match=${filter.type}`, {
      params: {
        rowIds: swimlaneIds.join(','),
        rowGroup: itemToString(rowGroup),
        columnGroup: itemToString(columnGroup),
      },
    });

    return swimlaneTransformer(result.kanbanResponseModelList, fieldList);
  }

  public async getTaskInSwimLane({
    offsetCol,
    rowId,
    colId,
    rowGroup,
    columnGroup,
    fieldList,
    projectId,
    filter,
  }: GetTaskInSwimLaneParam): Promise<GetTaskInSwimLaneResponse> {
    if (!columnGroup) {
      return Promise.reject();
    }
    const baseApi = projectId
      ? `/tasks/projects/${projectId}/kanbanV1ColForProjectBoard`
      : 'tasks/getlightkanbanColV1';

    const result = await this.get<GetTaskInSwimLaneResponse>(
      `${baseApi}?${getFilterString(filter)}&match=${filter.type}`,
      {
        params: {
          offsetCol,
          rowId,
          colId,
          rowGroup: itemToString(rowGroup),
          columnGroup: itemToString(columnGroup),
          limitCol: 10,
        },
      },
    );

    return columnTransformer(result, fieldList);
  }

  public async updateDragAndDrop({
    fromRowId,
    fromColId,
    toRowId,
    toColId,
    rowGroup,
    columnGroup,
    filter,
    taskId,
  }: UpdateDragAndDropParams): Promise<DragAndDropResponse> {
    if (!columnGroup) {
      return Promise.reject();
    }

    return this.get<DragAndDropResponse>(
      `tasks/getDragAndDropAllowed?${getFilterString(filter)}`,
      {
        params: {
          fromRowId,
          fromColId,
          toColId,
          toRowId,
          rowGroup: itemToString(rowGroup),
          columnGroup: itemToString(columnGroup),
          taskId,
        },
      },
    );
  }

  public fetchLayoutForTaskId = async ({
    taskId,
    rowGroup,
    columnGroup,
    filter,
    fieldList,
  }: CommonParams & {
    taskId: number;
    fieldList: Record<number, TaskField>;
  }): Promise<SocketPaginationParams<Task>> => {
    if (!columnGroup) {
      return Promise.reject();
    }

    const res = await this.get<RawFetchLayoutForTaskIdResponse>(
      `tasks/getSwimlaneDataForTaskId?${getFilterString(filter)}&match=${
        filter.type
      }`,
      {
        params: {
          taskId,
          rowGroup: itemToString(rowGroup),
          columnGroup: itemToString(columnGroup),
        },
      },
    );

    return {
      swimlaneData: res.swimlaneData.reduce((acc, swimlane) => {
        acc[swimlane.rowId] = {
          rowLabel: swimlane.rowLabel,
          columnData: Object.keys(swimlane.columnData).reduce((acc2, key2) => {
            acc2[key2] = {
              colLabel: swimlane.columnData[key2].colLabel,
            };

            return acc2;
          }, {}),
        };

        return acc;
      }, {}),

      taskDetails: {
        ...constructNormalizedTask({
          taskList: [res.taskDetails],
          fieldList,
          isLoading: false,
        }).byId[taskId][1],
        label: res.taskDetails.taskName,
      } as any,
    };
  };
}

export const KanbanApi = new KanbanHttpClient();
